import React, { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from 'react-query';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import Checkbox from 'material-ui/Checkbox';
import IconButton from 'material-ui/IconButton';
import IconMenu from 'material-ui/IconMenu';
import NavigationClose from 'material-ui/svg-icons/navigation/close';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import LinearProgress from 'material-ui/LinearProgress';
import {
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
} from 'material-ui/Table';
import { compareSuites, deleteSuite, getSuite, listSuites } from 'services/testRuns';
import { formatBytes, formatMetric, formatSeconds, formatSuiteLabel, isGitSha, runSelectionTip } from './metrics';
import StartDialog from './StartDialog';
import CompareResults from './CompareResults';
import { DocStatus, HoverTip, LetterLink } from './HoverTip';
import './styles.scss';

const BITBUCKET = 'https://bitbucket.org/shipnextmain';

function CommitLink({ project, hash }) {
    return (
        <a href={`${BITBUCKET}/${project}/commits/${hash}`} target="_blank" rel="noreferrer">
            {hash}
        </a>
    );
}

function ImageBuild({ tag }) {
    if (!tag) {
        return '—';
    }
    const match = String(tag).match(/build-(\d+)/);
    if (!match) {
        return tag;
    }
    return (
        <a
            href={`${BITBUCKET}/gate-web-service/pipelines/results/${match[1]}`}
            target="_blank"
            rel="noreferrer"
        >
            {tag}
        </a>
    );
}

function revisions(data, part) {
    return ['cargo', 'vessel'].flatMap((type) => {
        const slot = data.runVer && data.runVer[type];
        const hash = slot && slot[part] && slot[part].revision;
        return hash ? [{ type, hash: String(hash) }] : [];
    });
}

function ModelRevision({ data }) {
    const items = revisions(data, 'model');
    if (items.length === 0) {
        return null;
    }
    if (items.length === 1) {
        return (
            <span>
                model <CommitLink project={`${items[0].type}-model`} hash={items[0].hash} />
            </span>
        );
    }
    return items.map(item => (
        <span key={item.type}>
            {item.type} model <CommitLink project={`${item.type}-model`} hash={item.hash} />
        </span>
    ));
}

function GazetteerRevision({ data }) {
    const pinned = data.gazetteerRevision && isGitSha(data.gazetteerRevision)
        ? String(data.gazetteerRevision).trim()
        : '';
    if (pinned) {
        return (
            <span>
                gazetteers <CommitLink project="gazetteers" hash={pinned} />
            </span>
        );
    }
    const items = revisions(data, 'gazetteers');
    if (items.length === 0) {
        return null;
    }
    const unique = [...new Set(items.map(item => item.hash))];
    if (unique.length === 1) {
        return (
            <span>
                gazetteers <CommitLink project="gazetteers" hash={unique[0]} />
            </span>
        );
    }
    return items.map(item => (
        <span key={item.type}>
            {item.type} gazetteers <CommitLink project="gazetteers" hash={item.hash} />
        </span>
    ));
}

export default function SuitePage({ params, router, location }) {
    const queryClient = useQueryClient();
    const [deleting, setDeleting] = useState(false);
    const [startOpen, setStartOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [percentMode, setPercentMode] = useState(true);
    const compareTo = location.query && location.query.compare;
    const { data, error, isLoading } = useQuery(
        ['test-run', params.id],
        () => getSuite(params.id),
        { refetchInterval: deleting || compareTo ? false : 4000 },
    );
    const suitesQuery = useQuery('test-runs', listSuites);
    const compareQuery = useQuery(
        ['test-run-compare', compareTo, params.id],
        () => compareSuites(compareTo, params.id),
        { enabled: Boolean(compareTo) },
    );

    const onDelete = async () => {
        const running = data && (data.status === 'running' || data.status === 'collecting' || data.status === 'starting' || data.status === 'pending');
        const message = running
            ? 'Stop this run, release its queues and Swarm workers, and delete it and its results?'
            : 'Delete this run and its recognition results?';
        if (!window.confirm(message)) {
            return;
        }
        setDeleteError('');
        setDeleting(true);
        try {
            await deleteSuite(params.id);
            queryClient.invalidateQueries('test-runs');
            router.push('/bbr/runs');
        } catch (err) {
            setDeleteError(err.message || 'Failed to delete run');
            setDeleting(false);
        }
    };

    if (error) {
        return <div className="test-runs-page test-runs-error">{error.message}</div>;
    }
    if (isLoading || !data) {
        return <LinearProgress />;
    }

    const snapshot = data.documents || [];
    const perDoc = (data.metrics && data.metrics.perDoc) || {};
    const progress = data.progress || { done: 0, total: 0 };
    const held = Array.isArray(data.heldResources) ? data.heldResources : [];
    const currentId = data.id || data._id;
    const suites = suitesQuery.data || [];
    const compareTargets = suites.filter((row) => (
        row.status === 'completed' && row.id !== currentId
    ));
    const baseline = suites.find(row => row.id === compareTo)
        || (compareQuery.data && compareQuery.data.from);

    return (
        <div className="test-runs-page">
            <div className="test-runs-toolbar">
                <h2>
                    <Link to="/bbr/runs">Recognition tests</Link>
                    {data.corpusId ? (
                        <span>
                            {' / '}
                            <Link to={`/bbr/corporas/${data.corpusId}`}>{data.corpusName || data.corpusId}</Link>
                        </span>
                    ) : null}
                    {' / '}
                    {data.reason || data.id}
                </h2>
                <div className="test-runs-toolbar-actions">
                    {data.status === 'completed' ? (
                        <div className="test-runs-compare-base">
                            <HoverTip label={runSelectionTip(baseline)}>
                                <SelectField
                                    floatingLabelText={compareTo ? 'From (baseline)' : 'Compare against'}
                                    value={compareTo || null}
                                    onChange={(e, i, value) => {
                                        if (!value) {
                                            return;
                                        }
                                        router.push(`/bbr/runs/${currentId}?compare=${encodeURIComponent(value)}`);
                                    }}
                                    style={{ width: 280 }}
                                >
                                    {compareTargets.map(row => (
                                        <MenuItem key={row.id} value={row.id} primaryText={formatSuiteLabel(row)} />
                                    ))}
                                </SelectField>
                            </HoverTip>
                            {compareTo ? (
                                <HoverTip label="Remove">
                                    <IconButton onClick={() => router.push(`/bbr/runs/${currentId}`)}>
                                        <NavigationClose />
                                    </IconButton>
                                </HoverTip>
                            ) : null}
                        </div>
                    ) : null}
                    {data.status === 'completed' && compareTo ? (
                        <HoverTip label={runSelectionTip(data)}>
                            <SelectField
                                floatingLabelText="To (candidate)"
                                value={currentId}
                                disabled
                                style={{ width: 280 }}
                            >
                                <MenuItem value={currentId} primaryText={formatSuiteLabel(data)} />
                            </SelectField>
                        </HoverTip>
                    ) : null}
                    {data.status === 'completed' && compareTo ? (
                        <Checkbox
                            label="Percent"
                            checked={percentMode}
                            onCheck={(e, checked) => setPercentMode(checked)}
                            style={{ width: 'auto', marginBottom: 8 }}
                        />
                    ) : null}
                    <IconMenu
                        iconButtonElement={<IconButton><MoreVertIcon /></IconButton>}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        targetOrigin={{ horizontal: 'right', vertical: 'top' }}
                    >
                        <MenuItem
                            primaryText="New run from this"
                            onClick={() => setStartOpen(true)}
                        />
                        <MenuItem
                            primaryText={deleting ? 'Deleting…' : 'Delete run'}
                            disabled={deleting}
                            onClick={onDelete}
                        />
                    </IconMenu>
                </div>
            </div>
            {deleteError && <div className="test-runs-error">{deleteError}</div>}
            <div className="test-runs-doc-line">
                <DocStatus status={data.status} />
                <span>cargo <ImageBuild tag={data.cargoImageTag} /></span>
                <span>vessel <ImageBuild tag={data.vesselImageTag} /></span>
                <ModelRevision data={data} />
                <GazetteerRevision data={data} />
            </div>
            {held.length > 0 && (
                <p className="test-runs-hint">Held resources: {held.length} (queues/services still leased)</p>
            )}
            {(data.status === 'running' || data.status === 'collecting' || data.status === 'starting') && (
                <LinearProgress
                    mode="determinate"
                    value={progress.total ? (progress.done / progress.total) * 100 : 0}
                />
            )}
            {compareTo ? (
                compareQuery.error ? (
                    <div className="test-runs-error">{compareQuery.error.message}</div>
                ) : compareQuery.isLoading || !compareQuery.data ? (
                    <LinearProgress />
                ) : (
                    <CompareResults
                        data={compareQuery.data}
                        percentMode={percentMode}
                        onOpenDoc={(docId) => {
                            router.push(`/bbr/runs/${currentId}/docs/${docId}?compare=${encodeURIComponent(compareTo)}`);
                        }}
                    />
                )
            ) : (
            <Table selectable={false}>
                <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                    <TableRow>
                        <TableHeaderColumn style={{ width: 48 }} />
                        <TableHeaderColumn>Mail</TableHeaderColumn>
                        <TableHeaderColumn>Type</TableHeaderColumn>
                        <TableHeaderColumn>Matched</TableHeaderColumn>
                        <TableHeaderColumn>Annotations</TableHeaderColumn>
                        <TableHeaderColumn>LHS / RHS</TableHeaderColumn>
                        <TableHeaderColumn>Time</TableHeaderColumn>
                        <TableHeaderColumn>Heap after GC</TableHeaderColumn>
                        <TableHeaderColumn>newclarified</TableHeaderColumn>
                    </TableRow>
                </TableHeader>
                <TableBody displayRowCheckbox={false} showRowHover>
                    {snapshot.map((mail) => {
                        const type = mail.annotationType;
                        const stats = ((perDoc[mail.docId] || {})[type]) || {};
                        return (
                            <TableRow
                                key={mail.docId}
                                className="test-runs-doc-row"
                                selectable={false}
                                onClick={() => router.push(`/bbr/runs/${currentId}/docs/${mail.docId}`)}
                            >
                                <TableRowColumn style={{ width: 48 }}>
                                    <DocStatus status={mail.executionStatus} />
                                </TableRowColumn>
                                <TableRowColumn>
                                    <span className="test-runs-mail-cell">
                                        <HoverTip label={mail.subject}>
                                            <span>{mail.docId.slice(0, 8)}</span>
                                        </HoverTip>
                                        <LetterLink sourceMailId={mail.sourceMailId} />
                                    </span>
                                </TableRowColumn>
                                <TableRowColumn>{type}</TableRowColumn>
                                <TableRowColumn>{formatMetric(stats.totalMatchedAnnotations)}</TableRowColumn>
                                <TableRowColumn>{formatMetric(stats.annotationsCreated)}</TableRowColumn>
                                <TableRowColumn>{formatMetric(stats.lhsCandidates)} / {formatMetric(stats.rhsFirings)}</TableRowColumn>
                                <TableRowColumn>{formatSeconds(stats.timeSpent)}</TableRowColumn>
                                <TableRowColumn>{formatBytes(stats.heapUsedAfterJobGcBytes)}</TableRowColumn>
                                <TableRowColumn>—</TableRowColumn>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            )}
            <StartDialog
                open={startOpen}
                source={data}
                onClose={() => setStartOpen(false)}
                onStarted={(id) => {
                    setStartOpen(false);
                    router.push(`/bbr/runs/${id}`);
                }}
            />
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/SuitePage.jsx